/**
 * Evidence Requirements manager — per-project, per-status (Part 7, A7.1).
 *
 * Admin/manager view: configure which evidence each task must carry to enter
 * a given status. Block-state is enforced server-side; this UI just edits
 * the requirement set.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Plus, Trash2, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Switch } from '../ui/switch'
import { Textarea } from '../ui/textarea'
import { Badge } from '../ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { evidenceService, type EvidenceRequirement, type EvidenceType } from '../../services/evidence'
import type { ApiErrorLike } from '../../utils/apiError'

const EVIDENCE_TYPES: EvidenceType[] = ['photo', 'signature', 'file', 'approval', 'checklist', 'external_ref']

const DEFAULT_STATUSES = ['todo', 'in_progress', 'review', 'done', 'blocked']

interface Props {
  projectId: number
}

export function EvidenceRequirementsManager({ projectId }: Props) {
  const [items, setItems] = useState<EvidenceRequirement[]>([])
  const [loading, setLoading] = useState(true)
  const [showDialog, setShowDialog] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    status_name: 'done',
    evidence_type: 'photo' as EvidenceType,
    label: '',
    instructions: '',
    is_required: true,
  })

  useEffect(() => { load() }, [projectId])

  async function load() {
    setLoading(true)
    try { setItems(await evidenceService.listRequirements(projectId)) }
    catch { toast.error('Failed to load evidence requirements') }
    finally { setLoading(false) }
  }

  async function handleCreate() {
    if (!form.label.trim()) return
    setSaving(true)
    try {
      const created = await evidenceService.createRequirement(projectId, {
        status_name: form.status_name,
        evidence_type: form.evidence_type,
        label: form.label.trim(),
        instructions: form.instructions.trim() || null,
        is_required: form.is_required,
        position: items.length,
      })
      setItems(prev => [...prev, created])
      setShowDialog(false)
      setForm({ status_name: 'done', evidence_type: 'photo', label: '', instructions: '', is_required: true })
      toast.success('Requirement added')
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      toast.error(e?.response?.data?.detail || 'Failed to create requirement')
    } finally { setSaving(false) }
  }

  async function handleDelete(id: number) {
    try {
      await evidenceService.deleteRequirement(id)
      setItems(prev => prev.filter(i => i.id !== id))
    } catch { toast.error('Delete failed') }
  }

  const byStatus = items.reduce<Record<string, EvidenceRequirement[]>>((acc, r) => {
    (acc[r.status_name] ||= []).push(r); return acc
  }, {})

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="size-5 animate-spin text-muted-foreground" />
    </div>
  )

  return (
    <section aria-labelledby="evidence-reqs-heading" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 id="evidence-reqs-heading" className="text-base font-semibold">Completion Evidence</h2>
          <p className="text-xs text-muted-foreground">Block status transitions until required evidence is attached.</p>
        </div>
        <Button onClick={() => setShowDialog(true)} size="sm" className="min-h-[44px]">
          <Plus className="size-4 mr-1" /> Add Requirement
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          No evidence requirements configured. Tasks can change status freely.
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(byStatus).map(([status, reqs]) => (
            <div key={status} className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="capitalize">{status.replace(/_/g, ' ')}</Badge>
                <span className="text-xs text-muted-foreground">{reqs.length} requirement{reqs.length !== 1 ? 's' : ''}</span>
              </div>
              <ul className="space-y-2">
                {reqs.map(r => (
                  <li key={r.id} className="flex items-start gap-3 rounded-md border bg-background p-2">
                    <Badge variant="secondary" className="text-xs capitalize">{r.evidence_type.replace('_', ' ')}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{r.label}</span>
                        {r.is_required && <Badge variant="outline" className="text-xs">Required</Badge>}
                      </div>
                      {r.instructions && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{r.instructions}</p>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 min-h-[44px] min-w-[44px]"
                      aria-label={`Delete ${r.label}`}>
                      <Trash2 className="size-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Evidence Requirement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="req-status">Applies to status</Label>
              <Select value={form.status_name} onValueChange={v => setForm(f => ({ ...f, status_name: v }))}>
                <SelectTrigger id="req-status" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEFAULT_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="req-type">Evidence type</Label>
              <Select value={form.evidence_type} onValueChange={v => setForm(f => ({ ...f, evidence_type: v as EvidenceType }))}>
                <SelectTrigger id="req-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVIDENCE_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="req-label">Label</Label>
              <Input id="req-label" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Site photo, Inspector sign-off" className="mt-1 min-h-[44px]" />
            </div>
            <div>
              <Label htmlFor="req-instructions">Instructions (optional)</Label>
              <Textarea id="req-instructions" value={form.instructions}
                onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))}
                placeholder="What evidence should the assignee provide?" className="mt-1" rows={2} />
            </div>
            <div className="flex items-center gap-3">
              <Switch id="req-required" checked={form.is_required}
                onCheckedChange={v => setForm(f => ({ ...f, is_required: v }))} />
              <Label htmlFor="req-required" className="flex items-center gap-2">
                <AlertCircle className="size-3.5" /> Required — block transition without this
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="min-h-[44px]">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving || !form.label.trim()} className="min-h-[44px]">
              {saving && <Loader2 className="size-4 animate-spin mr-1" />} Add Requirement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
